import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { isBlockNoteTemplate, templatesModel } from '@/entities/templates';
import {
  normalizeContentLanguage,
  resolveContentLanguage,
  TRANSCRIPTOR_LANGUAGE,
} from '@/entities/transcriptor';

import { filterTemplateDataByLanguage } from '../lib';

// The schemas and templates a recording starts from. With Transcriptor v2:
// the organization's language only, and the draft of a summary without an
// active version when asked for. Without it: the interface language, or
// English when that language has no schema, and no Transcriptor v2 content.
export const useLocalizedTemplates = (includeSummaryDrafts = false) => {
  const { locale } = useIntl();

  const isConsultationTemplatesEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ConsultationTemplates,
    ),
  );
  const organizationLanguage = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationLanguage,
  );
  const organizationId = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationID,
  );
  const v2Enabled =
    useAppSelector((state) =>
      organizationModel.selectors.selectTranscriptorV2Enabled(
        state,
        organizationId,
      ),
    ) === true;

  const schemas = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  );
  const documentTemplates = useAppSelector(
    v2Enabled && includeSummaryDrafts
      ? templatesModel.selectors.selectDocumentTemplates
      : templatesModel.selectors.selectActiveDocumentTemplates,
  );

  const requestedLanguage = v2Enabled
    ? normalizeContentLanguage(organizationLanguage)
    : isConsultationTemplatesEnabled
      ? normalizeContentLanguage(locale)
      : resolveContentLanguage(locale);

  return useMemo(() => {
    if (v2Enabled) {
      const visibleTemplates = documentTemplates.filter(
        (template) =>
          (template.status === 'active' ||
            (includeSummaryDrafts &&
              template.transcriptor_v2 &&
              template.type === 'SUMMARY' &&
              template.status === 'draft' &&
              !documentTemplates.some(
                (other) =>
                  other.series_id === template.series_id &&
                  other.status === 'active',
              ))) &&
          (!template.organization_id ||
            template.organization_id === organizationId),
      );
      const localized = filterTemplateDataByLanguage({
        language: requestedLanguage,
        schemas: schemas.filter(
          (schema) =>
            !schema.organization_id ||
            schema.organization_id === organizationId,
        ),
        templates: visibleTemplates,
      });

      return {
        contentLanguage: requestedLanguage,
        schemas: localized.schemas.filter(
          (schema) =>
            normalizeContentLanguage(schema.language) === requestedLanguage,
        ),
        documentTemplates: localized.templates.filter(
          (template) =>
            normalizeContentLanguage(template.language) === requestedLanguage,
        ),
      };
    }

    const classicSchemas = schemas.filter((schema) => !schema.transcriptor_v2);
    const classicTemplates = documentTemplates.filter(
      (template) => !template.transcriptor_v2 && !isBlockNoteTemplate(template),
    );
    const localized = filterTemplateDataByLanguage({
      language: requestedLanguage,
      schemas: classicSchemas,
      templates: classicTemplates,
    });

    const shouldFallBackToEnglish =
      localized.schemas.length === 0 &&
      requestedLanguage !== TRANSCRIPTOR_LANGUAGE.English;

    if (!shouldFallBackToEnglish) {
      return {
        contentLanguage: requestedLanguage,
        schemas: localized.schemas,
        documentTemplates: localized.templates,
      };
    }

    const fallback = filterTemplateDataByLanguage({
      language: TRANSCRIPTOR_LANGUAGE.English,
      schemas: classicSchemas,
      templates: classicTemplates,
    });

    return {
      contentLanguage: TRANSCRIPTOR_LANGUAGE.English,
      schemas: fallback.schemas,
      documentTemplates: fallback.templates,
    };
  }, [
    requestedLanguage,
    schemas,
    documentTemplates,
    v2Enabled,
    organizationId,
    includeSummaryDrafts,
  ]);
};
